// Shared review-thread data hook. Both the sidebar (CommentsPanel) and the
// board-pin overlay (CommentPinOverlay) call this with the same key, so
// react-query dedupes the fetch and any mutation invalidates both surfaces.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listComments,
  postComment,
  resolveComment,
  deleteComment,
  toggleCommentReaction,
  toggleProposalReaction,
  type CommentAnchor,
  type ReviewThreads,
} from "./proposalsApi.js";
import type { CommentThreadHandlers } from "./CommentThread.js";
import { useMe } from "../api/hooks/useMe.js";
import { useToast } from "../ui/Toast.js";

export interface ReviewThreadsController {
  readonly threads: ReviewThreads | undefined;
  readonly myId: string | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly busy: boolean;
  /** Post a root/general comment or an anchored root. */
  postComment(body: string, opts?: { anchor?: CommentAnchor }): void;
  /** Toggle the proposal-level reaction. */
  reactProposal(emoji: string): void;
  /** Handlers for a `<CommentThread>`. */
  readonly handlers: CommentThreadHandlers;
}

export function useReviewThreads(
  slug: string,
  repo: string,
  proposalId: string,
): ReviewThreadsController {
  const qc = useQueryClient();
  const toast = useToast();
  const myId = useMe().data?.id ?? null;
  const key = ["comments", slug, repo, proposalId] as const;

  const q = useQuery({
    queryKey: key,
    queryFn: () => listComments(slug, repo, proposalId),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const onError = (e: Error) => toast.error(e.message);

  const post = useMutation({
    mutationFn: (v: { body: string; parentCommentId?: string; anchor?: CommentAnchor }) =>
      postComment(slug, repo, proposalId, v.body, {
        parentCommentId: v.parentCommentId,
        anchor: v.anchor,
      }),
    onSuccess: invalidate,
    onError,
  });
  const resolve = useMutation({
    mutationFn: (v: { id: string; resolved: boolean }) =>
      resolveComment(slug, repo, proposalId, v.id, v.resolved),
    onSuccess: invalidate,
    onError,
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteComment(slug, repo, proposalId, id),
    onSuccess: invalidate,
    onError,
  });
  const reactComment = useMutation({
    mutationFn: (v: { id: string; emoji: string }) =>
      toggleCommentReaction(slug, repo, proposalId, v.id, v.emoji),
    onSuccess: invalidate,
    onError,
  });
  const reactProposalM = useMutation({
    mutationFn: (emoji: string) => toggleProposalReaction(slug, repo, proposalId, emoji),
    onSuccess: invalidate,
    onError,
  });

  return {
    threads: q.data,
    myId,
    isLoading: q.isLoading,
    isError: q.isError,
    error: (q.error as Error) ?? null,
    busy: post.isPending || resolve.isPending || del.isPending,
    postComment: (body, opts) => post.mutate({ body, anchor: opts?.anchor }),
    reactProposal: (emoji) => reactProposalM.mutate(emoji),
    handlers: {
      onReply: (rootId, body) => post.mutate({ body, parentCommentId: rootId }),
      onResolve: (rootId, resolved) => resolve.mutate({ id: rootId, resolved }),
      onDelete: (commentId) => del.mutate(commentId),
      onReact: (commentId, emoji) => reactComment.mutate({ id: commentId, emoji }),
    },
  };
}
